import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import { ButtonInput } from "./ButtonInput";
import { VehicleIdentifierType } from "../../../models/Vehicle";
import { identifierMaxLength, identifierPlaceholder } from "../../../utils/vehicleIdentifier";

interface RegScanInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Fired on Enter and when the button is clicked. */
  onSubmit: () => void;
  buttonLabel: string;
  label?: string;
  placeholder?: string;
  isLoading?: boolean;
  disabled?: boolean;
  /** Registration keeps today's label and placeholder; Vin asks for and caps a 17-character VIN. */
  identifierType?: VehicleIdentifierType;
}

/**
 * "Scan or enter vehicle reg" input, styled to match the Station Manager
 * picker: uppercase label, search icon, uppercased value and a submit button.
 */
export default function RegScanInput({
  value,
  onChange,
  onSubmit,
  buttonLabel,
  label,
  placeholder,
  isLoading = false,
  disabled = false,
  identifierType = "Registration",
}: RegScanInputProps) {
  const isVin = identifierType === "Vin";
  label ??= isVin ? "Scan or enter VIN" : "Scan or enter vehicle reg";
  placeholder ??= isVin ? identifierPlaceholder("Vin") : "e.g. KT68XVX";

  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </label>
      <div className="mt-1 flex items-center gap-x-2">
        <div className="relative min-w-0 flex-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
          <input
            type="text"
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSubmit();
            }}
            placeholder={placeholder}
            maxLength={identifierMaxLength(identifierType)}
            className="w-full rounded-md border-gray-300 pl-10 text-sm uppercase focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100"
          />
        </div>

        <ButtonInput
          label={buttonLabel}
          isSubmit={false}
          onClick={onSubmit}
          isLoading={isLoading}
          disabled={disabled || !value.trim()}
          classes="shrink-0"
        />
      </div>
    </div>
  );
}
